import React, { useState, useEffect } from 'react';
import { getProducts, searchProducts } from '../services/api';
import ProductCard from './ProductCard';
import SearchBar from './SearchBar';
import { PageHeader, Button } from './ui';
import './ProductList.css';

const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const productsPerPage = 100;

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        if (debouncedSearchTerm.trim()) {
          // Use search API for search queries
          setIsSearching(true);
          const data = await searchProducts(debouncedSearchTerm, { limit: 200 }); // Higher limit for search results
          setProducts(data);
          setTotalProducts(data.length);
        } else {
          // Use regular products API for browsing
          setIsSearching(false);
          const offset = (currentPage - 1) * productsPerPage;
          const data = await getProducts({ limit: productsPerPage, offset });
          
          // Handle new API response format
          if (data.products) {
            setProducts(data.products);
            setTotalProducts(data.total);
          } else {
            // Fallback for old format
            setProducts(data);
          }
        }
      } catch (err) {
        setError('Failed to load products. Please try again later.');
        console.error('Error fetching products:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentPage, debouncedSearchTerm]);

  // Remove the client-side filtering since we're now using backend search

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo(0, 0); // Scroll to top when changing pages
  };

  const totalPages = Math.ceil(totalProducts / productsPerPage);

  if (loading) {
    return <div className="loading">Loading products...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="product-list">
      <PageHeader
        title="Products"
        sub="The master catalog staged from Akeneo, ready to compare and push to BigCommerce."
        actions={
          <>
            <SearchBar onSearch={handleSearch} />
            <Button variant="secondary" to="/products/categorisation">Categorisation</Button>
          </>
        }
      />
      
      <div className="product-stats">
        {isSearching ? (
          `Found ${products.length} products matching "${debouncedSearchTerm}"`
        ) : (
          `Showing ${products.length} of ${totalProducts.toLocaleString()} products (Page ${currentPage} of ${totalPages})`
        )}
      </div>

      <div className="product-grid">
        {products.map((product) => (
          <ProductCard key={product.id} product={product} />
        ))}
      </div>

      {products.length === 0 && !loading && (
        <div className="no-products">
          <p>No products found matching your search criteria.</p>
        </div>
      )}

      {!isSearching && totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            Previous
          </button>
          
          <div className="pagination-info">
            <span>Page {currentPage} of {totalPages}</span>
          </div>
          
          <button 
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default ProductList;
